import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import ArrowUpIcon from "@src/lib/icons/18px/arrow-up";
import BoltOutlineIcon from "@src/lib/icons/18px/bolt-outline";
import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";
import LightbulbOutlineIcon from "@src/lib/icons/18px/lightbulb-outline";
import { Button } from "@src/lib/ui/Button";
import {
  DictationRecordingBar,
  DictationToggle,
  WithDictation,
} from "@src/features/aiAssist/DictationButton";
import { Dictation } from "@src/features/aiAssist/hooks/useDictation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import Text from "@src/lib/ui/Text";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import { cn } from "@src/lib/utils/cn";
import { OpusAgentModel } from "@src/types.generated";
import {
  OPUS_AGENT_MODEL_PRESETS,
  OPUS_AGENT_MODEL_RATING_MAX,
  KnownOpusAgentModel,
  OPUS_AGENT_MODEL_RATINGS,
  OPUS_AGENT_PICKER_MODELS,
  OPUS_AGENT_PRESET_DESCRIPTIONS,
  OPUS_AGENT_PRESET_LABELS,
  OPUS_AGENT_PRESET_ORDER,
  OpusAgentModelPreset,
  OpusAgentModelRatingValue,
  opusAgentModelLabel,
  opusAgentPickerLabel,
  opusAgentPresetForModel,
} from "./models";
import {
  OpusAgentVoiceBar,
  OpusAgentVoiceToggle,
} from "./voice/OpusAgentVoiceBar";
import { OpusAgentVoice } from "./voice/useOpusAgentVoice";
import { OpusAgentAttachMenu } from "./attachments/OpusAgentAttachMenu";
import { OpusAgentAttachmentPill } from "./attachments/OpusAgentAttachmentPill";
import { canRetryAttachment } from "./attachments/sendGate";
import {
  MAX_OPUS_AGENT_ATTACHMENTS,
  OPUS_AGENT_ATTACHMENT_EXTENSIONS,
  type UseOpusAgentAttachmentsReturn,
} from "./attachments/useOpusAgentAttachments";

const RATING_STEPS = Array.from(
  { length: OPUS_AGENT_MODEL_RATING_MAX },
  (_, index) => index + 1,
);

// Compact 1–5 meter for a model row: a glyph plus five dots, read out as
// e.g. "Speed 4 of 5". Kept tiny and muted so a row stays one line at the
// drawer's 320px minimum width.
const RatingMeter: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: OpusAgentModelRatingValue;
}> = ({ icon, label, value }) => (
  <span
    role="img"
    aria-label={`${label} ${value} of ${OPUS_AGENT_MODEL_RATING_MAX}`}
    className="flex items-center gap-px"
  >
    <span className="mr-0.5 flex">{icon}</span>
    {RATING_STEPS.map((step) => (
      <span
        key={step}
        className={cn(
          "size-1 rounded-full",
          step <= value ? "bg-icon-secondary" : "bg-border-primary",
        )}
      />
    ))}
  </span>
);

interface OpusAgentComposerProps {
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  isRunning?: boolean;
  onStop?: () => void;
  // The model the next turn runs on (null until the server default is known)
  // and the server default, so the menu can mark it.
  effectiveModel?: OpusAgentModel | null;
  defaultModel?: OpusAgentModel | null;
  // Allowed any time, mid-run included: it applies to the next turn. Absent
  // hides the model picker (the "Create X with AI" dialog has no chat yet).
  onSelectModel?: (model: OpusAgentModel) => void;
  // Voice mode (Ff.OpusAgentVoice); null hides it.
  voice?: OpusAgentVoice | null;
  // File and Opus-item attachments; null hides the attach button.
  attachments?: UseOpusAgentAttachmentsReturn | null;
  placeholder?: string;
  // Text beside the send arrow; absent keeps the icon-only button.
  submitLabel?: string;
  // Locks typing and sending, e.g. while the dialog creates its item.
  disabled?: boolean;
  autoFocus?: boolean;
  inputClassName?: string;
}

// Styling duplicated from the Ask Opus ChatComposer (no brand selector).
// Send waits for every attachment pill to be ready. Unlike Ask Opus, submitting while a turn is RUNNING is allowed:
// the new message supersedes the running turn server-side.
export const OpusAgentComposer: React.FC<OpusAgentComposerProps> = ({
  input,
  onInputChange,
  onSubmit,
  isRunning = false,
  onStop,
  effectiveModel = null,
  defaultModel = null,
  onSelectModel,
  voice = null,
  attachments = null,
  placeholder = "Ask me anything...",
  submitLabel,
  disabled = false,
  autoFocus = false,
  inputClassName,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Files the last pick could not add (past the per-message limit).
  const [skippedFiles, setSkippedFiles] = useState(0);
  // The notice is about the last pick: gone once the pills are.
  const pillCount = attachments?.pills.length ?? 0;
  useEffect(() => {
    if (pillCount === 0) setSkippedFiles(0);
  }, [pillCount]);
  // Files dropped on the composer or pasted into it go the paperclip's way:
  // same types, size limit and refusals (addFiles marks a refused file
  // failed), so no `accept` here.
  const addFiles = (files: File[]) => {
    if (attachments && !disabled && files.length > 0)
      setSkippedFiles(attachments.addFiles(files));
  };
  const { getRootProps, isDragActive, rootRef } = useDropzone({
    onDrop: addFiles,
    noClick: true,
    noKeyboard: true,
    disabled: !attachments || disabled,
    // Its document guard cancels every drop, text included; ours below
    // only cancels files.
    preventDropOnDocument: false,
  });
  // While attaching is on, a file dropped outside the composer is refused
  // rather than opened by the browser, which would lose the draft. Only a
  // drag nothing else claimed: other drop zones cancel dragover themselves
  // (React runs before this document listener), editors take their own
  // drops, file inputs take files, and text and links pass untouched.
  const attachingOn = !!attachments;
  useEffect(() => {
    if (!attachingOn) return;
    const refuseStrayFile = (event: DragEvent) => {
      if (event.defaultPrevented || !dragHasFiles(event.dataTransfer)) return;
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target)) return;
      if (target instanceof HTMLInputElement && target.type === "file") return;
      if (target instanceof HTMLElement && target.isContentEditable) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "none";
    };
    document.addEventListener("dragover", refuseStrayFile);
    document.addEventListener("drop", refuseStrayFile);
    return () => {
      document.removeEventListener("dragover", refuseStrayFile);
      document.removeEventListener("drop", refuseStrayFile);
    };
  }, [attachingOn, rootRef]);
  // A text drag (moving words in the draft, a link from the thread) skips
  // the dropzone, so the textarea's own drop still works.
  const skipUnlessFiles = (event: React.DragEvent<HTMLElement>) => {
    if (!dragHasFiles(event.dataTransfer)) event.stopPropagation();
  };
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const hasMountedRef = useRef(false);
  useEffect(() => {
    hasMountedRef.current = true;
  }, []);
  // Also run on mount: the textarea remounts when the dictation bar closes.
  const attachTextarea = useCallback((textarea: HTMLTextAreaElement | null) => {
    textareaRef.current = textarea;
    if (textarea) autosize(textarea);
  }, []);

  const MAX_TEXTAREA_HEIGHT = 240;

  useEffect(() => {
    if (textareaRef.current) autosize(textareaRef.current);
  }, [input]);

  const isOverflowing = textareaRef.current
    ? textareaRef.current.scrollHeight > MAX_TEXTAREA_HEIGHT
    : false;

  const hasDraft = input.trim().length > 0;
  const activePreset = effectiveModel
    ? opusAgentPresetForModel(effectiveModel)
    : null;

  const gate = attachments?.gate ?? null;
  const sendBlocked = !!gate?.blocked;
  const sendBlockedReasonId = useId();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (hasDraft && !sendBlocked && !disabled) {
      onSubmit();
    }
  };

  const renderInputBox = (dictation: Dictation | null) => {
    const isDictating = !!dictation && dictation.status !== "idle";
    const isVoiceActive = !!voice?.isActive;
    const hasTray =
      !!attachments && (attachments.pills.length > 0 || skippedFiles > 0);
    return (
      <div
        {...getRootProps({
          onDragEnter: skipUnlessFiles,
          onDragOver: skipUnlessFiles,
          onDragLeave: skipUnlessFiles,
          onDrop: skipUnlessFiles,
          className: cn(
            "relative z-10 flex flex-col overflow-hidden rounded-xl shadow-default transition-shadow",
            // With attachments, a gray shell holds the white field and,
            // under it, the tray of attachment cards.
            hasTray ? "bg-bg-tertiary p-0.5" : "bg-bg-primary",
            isDragActive && "ring-1 ring-brand-border",
          ),
        })}
        data-testid="opus-agent-composer-drop-zone"
      >
        {isDragActive && attachments && (
          // Opaque, so the draft underneath doesn't show through the tint.
          <div
            className="pointer-events-none absolute inset-0 z-20 bg-bg-primary p-1"
            data-testid="opus-agent-composer-drop-overlay"
          >
            <div className="flex size-full items-center justify-center rounded-md-2 border-2 border-dashed border-brand-border bg-brand-bg">
              <Text variant="fine-print-title" className="text-brand-text">
                {attachments.atLimit
                  ? `Up to ${MAX_OPUS_AGENT_ATTACHMENTS} attachments per message`
                  : "Drop files to attach"}
              </Text>
            </div>
          </div>
        )}
        <div
          className={cn(
            "flex flex-col overflow-hidden bg-bg-primary",
            hasTray && "rounded-lg shadow-default",
          )}
        >
          {isVoiceActive && voice ? (
            <OpusAgentVoiceBar voice={voice} />
          ) : isDictating && dictation ? (
            <DictationRecordingBar
              dictation={dictation}
              restoreFocusTo={() => textareaRef.current}
              className="min-h-[40px] px-2 pt-2"
            />
          ) : (
            <textarea
              ref={attachTextarea}
              value={input}
              onChange={(e) => onInputChange(e.target.value)}
              onPaste={(e) => {
                const files = Array.from(e.clipboardData.files);
                if (!attachments || files.length === 0) return;
                // Rich text (cells from a spreadsheet, a paragraph from a doc)
                // can carry a picture of itself too: paste the text. A copied
                // file or image has no such pair (at most its name as text).
                const types = Array.from(e.clipboardData.types);
                if (types.includes("text/plain") && types.includes("text/html"))
                  return;
                e.preventDefault();
                addFiles(files);
              }}
              placeholder={placeholder}
              rows={1}
              disabled={disabled}
              // First mount only: the textarea remounts when the dictation
              // bar closes, and the bar restores focus itself then.
              autoFocus={autoFocus && !hasMountedRef.current}
              data-testid="chat-composer-input"
              className={cn(
                "max-h-60 min-h-10 w-full resize-none rounded-none border-none bg-bg-primary p-2 text-s-v4 font-normal outline-none placeholder:text-text-tertiary focus:ring-0 disabled:opacity-50",
                inputClassName,
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />
          )}
          <div
            className={cn(
              "flex items-end justify-between p-2 transition-all duration-200 ease-in-out",
              isOverflowing && "border-t border-border-primary",
            )}
          >
            <div className="flex flex-1 items-center">
              {attachments && (
                <>
                  <OpusAgentAttachMenu
                    disabled={attachments.atLimit || disabled}
                    onSelectLibraryItem={attachments.addLibraryItem}
                    onUploadClick={() => fileInputRef.current?.click()}
                    excludeLibraryItemIds={attachments.pills.flatMap((pill) =>
                      pill.libraryItem ? [pill.libraryItem.id] : [],
                    )}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    accept={OPUS_AGENT_ATTACHMENT_EXTENSIONS.join(",")}
                    data-testid="opus-agent-attach-file-input"
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      // Reset so picking the same file again still fires.
                      e.target.value = "";
                      addFiles(files);
                    }}
                  />
                </>
              )}
              {onSelectModel && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label="Model"
                      data-testid="opus-agent-model-picker"
                      className="gap-0.5 pr-1.5"
                    >
                      {effectiveModel
                        ? opusAgentPickerLabel(effectiveModel)
                        : "Model"}
                      <ChevronDownIcon className="size-3 text-icon-tertiary" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" side="top">
                    {OPUS_AGENT_PRESET_ORDER.map((preset) => (
                      <PresetMenuItem
                        key={preset}
                        preset={preset}
                        checked={activePreset === preset}
                        onSelect={() =>
                          onSelectModel(OPUS_AGENT_MODEL_PRESETS[preset])
                        }
                      />
                    ))}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger data-testid="opus-agent-model-preset-advanced">
                        <span className="whitespace-nowrap">Advanced</span>
                        {effectiveModel && !activePreset ? (
                          <CheckmarkIcon
                            className="ml-auto size-3.5 shrink-0 text-icon-primary"
                            data-testid="opus-agent-model-check"
                          />
                        ) : null}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {OPUS_AGENT_PICKER_MODELS.map((model) => (
                          <ModelMenuItem
                            key={model}
                            model={model}
                            isDefault={model === defaultModel}
                            checked={model === effectiveModel}
                            onSelect={() => onSelectModel(model)}
                          />
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {/* While a turn runs the button follows the draft, so pointer and
              keyboard agree: nothing typed → Stop; something typed → Send
              (Enter's action) as the primary, with Stop beside it. */}
            <div className="ml-2 flex items-center gap-1">
              {voice && !isVoiceActive && !isDictating && (
                <OpusAgentVoiceToggle voice={voice} />
              )}
              {dictation && !isDictating && !isVoiceActive && (
                <DictationToggle dictation={dictation} disabled={disabled} />
              )}
              {isRunning && onStop && (
                <Button
                  type="button"
                  variant={hasDraft ? "ghost" : "secondary"}
                  // 28px, the send button's size: swapping one for the other
                  // must not change the composer's height.
                  size="icon-sm"
                  onClick={onStop}
                  aria-label="Stop"
                  data-testid="opus-agent-stop"
                  className={cn(hasDraft && "text-icon-secondary")}
                >
                  <CircleStopIcon />
                </Button>
              )}
              {!isVoiceActive && (!isRunning || hasDraft || isDictating) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* Blocked by attachments, the button stays focusable
                      (aria-disabled, not disabled) so hover and keyboard
                      both reach the tooltip saying why. */}
                    <Button
                      size={submitLabel ? "sm" : "icon-sm"}
                      // While recording, send stops, transcribes, then sends.
                      onClick={(e) => {
                        if (sendBlocked) return;
                        if (dictation?.status === "recording")
                          dictation.stopAndSubmit();
                        else handleSubmit(e);
                      }}
                      disabled={
                        disabled ||
                        (!sendBlocked &&
                          (dictation?.status === "recording"
                            ? false
                            : dictation?.status === "transcribing" ||
                              !hasDraft))
                      }
                      aria-disabled={sendBlocked || undefined}
                      aria-label={submitLabel ?? "Send"}
                      aria-describedby={
                        sendBlocked ? sendBlockedReasonId : undefined
                      }
                      data-testid="opus-agent-send"
                      className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:shadow-none"
                    >
                      {submitLabel}
                      <ArrowUpIcon />
                    </Button>
                  </TooltipTrigger>
                  {sendBlocked && gate?.reason && (
                    <TooltipContent
                      side="top"
                      align="end"
                      data-testid="opus-agent-send-blocked-reason"
                    >
                      {gate.reason}
                    </TooltipContent>
                  )}
                </Tooltip>
              )}
              {/* Announces why send waits, as the footer text used to. */}
              <span id={sendBlockedReasonId} className="sr-only" role="status">
                {sendBlocked ? gate?.reason : null}
              </span>
            </div>
          </div>
        </div>
        {/* Always mounted while attaching is on, so the live region exists
            before its first pill and screen readers hear it arrive. */}
        {attachments && (
          <div
            className={cn("flex flex-col gap-1", hasTray && "p-1")}
            data-testid="opus-agent-attachment-pills"
            aria-live="polite"
          >
            {attachments.pills.map((pill) => (
              <OpusAgentAttachmentPill
                key={pill.key}
                state={pill.state}
                detail={pill.detail}
                uploadPercent={pill.uploadPercent}
                status={pill.status}
                fileName={pill.fileName}
                media={pill.media}
                libraryItem={pill.libraryItem}
                // Disabled, the pills are what gets sent: no edits.
                onRemove={
                  disabled
                    ? undefined
                    : () => {
                        attachments.remove(pill.key);
                        // The remove button goes away with its pill: keep focus
                        // in the composer rather than dropping it on the page.
                        textareaRef.current?.focus();
                      }
                }
                onRetry={
                  !disabled && canRetryAttachment(pill)
                    ? () => {
                        attachments.retry(pill.key);
                        // Retry goes away as the pill leaves failed.
                        textareaRef.current?.focus();
                      }
                    : undefined
                }
                fill
                // White on the gray tray; 12 − (2 + 4) inset → rounded-md.
                className={cn(
                  "rounded-md",
                  pill.state !== "failed" && "bg-bg-primary shadow-default",
                )}
                data-testid="opus-agent-attachment-pill"
              />
            ))}
            {skippedFiles > 0 && (
              <p
                className="w-full px-1 text-2xs-v4 text-text-tertiary"
                data-testid="opus-agent-attachment-limit"
              >
                {`Up to ${MAX_OPUS_AGENT_ATTACHMENTS} attachments per message; ${skippedFiles} not added.`}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative mx-auto flex w-full max-w-2xl flex-col">
      <WithDictation
        value={input}
        onChange={onInputChange}
        onSubmit={onSubmit}
        disabled={disabled}
      >
        {renderInputBox}
      </WithDictation>
    </div>
  );
};

const PresetMenuItem: React.FC<{
  preset: OpusAgentModelPreset;
  checked: boolean;
  onSelect: () => void;
}> = ({ preset, checked, onSelect }) => (
  <DropdownMenuItem
    data-testid={`opus-agent-model-preset-${preset}`}
    className="items-start justify-between gap-3"
    onSelect={onSelect}
  >
    <span className="flex min-w-0 flex-col">
      <span className="whitespace-nowrap">
        {OPUS_AGENT_PRESET_LABELS[preset]}
      </span>
      <span className="text-2xs-v4 whitespace-normal text-text-secondary">
        {OPUS_AGENT_PRESET_DESCRIPTIONS[preset]}
      </span>
    </span>
    {checked ? (
      <CheckmarkIcon
        className="mt-0.5 size-3.5 shrink-0 text-icon-primary"
        data-testid="opus-agent-model-check"
      />
    ) : (
      <span className="size-3.5 shrink-0" />
    )}
  </DropdownMenuItem>
);

const ModelMenuItem: React.FC<{
  model: KnownOpusAgentModel;
  isDefault: boolean;
  checked: boolean;
  onSelect: () => void;
}> = ({ model, isDefault, checked, onSelect }) => (
  <DropdownMenuItem
    data-testid={`opus-agent-model-option-${model}`}
    className="justify-between gap-3"
    onSelect={onSelect}
  >
    <span className="whitespace-nowrap">
      {opusAgentModelLabel(model)}
      {isDefault && <span className="text-text-secondary"> · default</span>}
    </span>
    <span className="flex shrink-0 items-center gap-2">
      <RatingMeter
        icon={<BoltOutlineIcon className="size-3 text-icon-secondary" />}
        label="Speed"
        value={OPUS_AGENT_MODEL_RATINGS[model].speed}
      />
      <RatingMeter
        icon={<LightbulbOutlineIcon className="size-3 text-icon-secondary" />}
        label="Intelligence"
        value={OPUS_AGENT_MODEL_RATINGS[model].intelligence}
      />
      {checked ? (
        <CheckmarkIcon
          className="size-3.5 shrink-0 text-icon-primary"
          data-testid="opus-agent-model-check"
        />
      ) : (
        <span className="size-3.5 shrink-0" />
      )}
    </span>
  </DropdownMenuItem>
);

// A drag carrying files, as opposed to selected text or a link.
const dragHasFiles = (dataTransfer: DataTransfer | null): boolean =>
  !!dataTransfer && Array.from(dataTransfer.types).includes("Files");

const autosize = (textarea: HTMLTextAreaElement) => {
  textarea.style.height = "auto";
  textarea.style.height = `${textarea.scrollHeight}px`;
};

const CircleStopIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      width="16"
      height="16"
    >
      <rect width="10" height="10" x="3" y="3" rx="2" />
    </svg>
  );
};
